import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { FONT_SIZE, GRID_RADIUS, MOTION, STATUS } from "../../../../theme/tokens";
import type { NurseFullRole } from "../../../../utils/models";
import type { ShiftMatchChange } from "./shiftMatchKey";

type UpdateShiftsWarningProps = {
	changes: ShiftMatchChange[];
};

// Mount entrance: fade + lift, transform/opacity only; reduced-motion drops it. The surrounding
// Collapse owns the height, so this only softens the arrival of the content inside it.
const containerSx: SxProps<Theme> = {
	display: "flex",
	gap: 1,
	width: "100%",
	padding: 1,
	borderRadius: GRID_RADIUS.BADGE,
	backgroundColor: STATUS.WARNING.light,
	color: STATUS.WARNING.dark,
	"@keyframes updateShiftsWarningEnter": {
		from: { opacity: 0, transform: "translateY(-4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `updateShiftsWarningEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const headlineSx = { fontWeight: 600, fontSize: FONT_SIZE.CAPTION, lineHeight: 1.34 } as const;
const changeSx = { fontSize: FONT_SIZE.CAPTION, lineHeight: 1.34 } as const;

const formatRoles = (roles: (NurseFullRole | "all")[], allLabel: string): string => roles.map((role) => (role === "all" ? allLabel : role)).join(", ");

/**
 * Shown when the user edits an "out of config" row away from the values it was pre-filled with.
 * Those values are the only thing tying the row's existing recurring shifts to the config being
 * created, so changing them rewrites real shifts — this spells out exactly which parts move.
 */
export const UpdateShiftsWarning: FC<UpdateShiftsWarningProps> = ({ changes }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();

	// The caller collapses this shut by emptying `changes`, and the collapse outlives that by a
	// frame or two. Hold the last non-empty set so the list slides away intact instead of blanking
	// first. (Adjusting state during render — the documented alternative to an effect here.)
	const [visibleChanges, setVisibleChanges] = useState<ShiftMatchChange[]>(changes);
	if (changes.length > 0 && changes !== visibleChanges) {
		setVisibleChanges(changes);
	}

	// Not useJobs(): a job id taken off an existing shift may no longer be enabled for the
	// department, and it still has to be nameable here.
	const describeJob = (jobId: string): string => {
		if (!jobId) return t("schedule.template.v3.unassignedJob");
		const job = (chosenDepartment.jobs ?? []).find((candidate) => candidate.id === jobId);
		// DEFAULT: fall back to the raw id when the job is unknown to this department — an opaque
		// id still tells the user something changed, where an empty string would read as "nothing".
		return job?.displayName ?? jobId;
	};

	const describe = (change: ShiftMatchChange): { label: string; from: string; to: string } => {
		const allLabel = t("schedule.template.v3.filter.allRoles");
		switch (change.field) {
			case "name":
				return { label: t("schedule.template.v3.editor.updateShifts.field.name"), from: change.from, to: change.to };
			case "hours":
				return {
					label: t("schedule.template.v3.editor.updateShifts.field.hours"),
					from: `${change.from.start}–${change.from.end}`,
					to: `${change.to.start}–${change.to.end}`,
				};
			case "roles":
				return {
					label: t("schedule.template.v3.editor.updateShifts.field.roles"),
					from: formatRoles(change.from, allLabel),
					to: formatRoles(change.to, allLabel),
				};
			case "job":
				return {
					label: t("schedule.template.v3.editor.updateShifts.field.job"),
					from: describeJob(change.from),
					to: describeJob(change.to),
				};
		}
	};

	return (
		<Box sx={containerSx} data-testid="location-update-shifts-warning">
			<WarningAmberRoundedIcon fontSize="small" />
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				<Typography sx={headlineSx}>{t("schedule.template.v3.editor.updateShifts.warning")}</Typography>
				{visibleChanges.map((change) => {
					const { label, from, to } = describe(change);
					return (
						<Typography key={change.field} sx={changeSx}>
							{label}: {from} → {to}
						</Typography>
					);
				})}
			</Box>
		</Box>
	);
};
